/**
 * useAdminApi — Sub-sprint A (RL-FRONTEND-SHELL-V0).
 *
 * Thin wrapper over the existing `api` axios instance that:
 *   - auto-attaches the admin JWT (handled by the global interceptor in
 *     `@/lib/api`, already configured),
 *   - exposes ergonomic { data, loading, error, reload } state for the
 *     four read-only admin pages,
 *   - surfaces 401 by redirecting to `/login`,
 *   - emits a single toast on non-401 errors via `sonner`.
 *
 * Read-only by design for v0. Mutation hooks (POST/PATCH) will land in
 * Sub-sprint B/C with explicit dry-run + confirmation_token flow.
 */
import { useCallback, useEffect, useReducer } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const initialState = { data: null, error: null, loading: true };

function reducer(state, action) {
  switch (action.type) {
    case "start":
      return { data: null, error: null, loading: true };
    case "ok":
      return { data: action.payload, error: null, loading: false };
    case "fail":
      return { data: null, error: action.payload, loading: false };
    case "skip":
      return { data: null, error: null, loading: false };
    default:
      return state;
  }
}

export function useAdminApi(path, { skip = false, params = null } = {}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [reloadKey, bumpReload] = useReducer((k) => k + 1, 0);

  const reload = useCallback(() => bumpReload(), []);

  useEffect(() => {
    if (skip || !path) {
      dispatch({ type: "skip" });
      return undefined;
    }
    let alive = true;
    dispatch({ type: "start" });
    api
      .get(path, params ? { params } : undefined)
      .then((r) => {
        if (!alive) return;
        dispatch({ type: "ok", payload: r.data });
      })
      .catch((e) => {
        if (!alive) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || e?.message || "unknown";
        if (status === 401) {
          window.location.assign("/login");
          return;
        }
        dispatch({ type: "fail", payload: { status, detail } });
        toast.error(`Admin API ${path} failed: ${detail}`);
      });
    return () => {
      alive = false;
    };
  }, [path, JSON.stringify(params), reloadKey, skip]);

  return { ...state, reload };
}

export default useAdminApi;
